import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
    clearUserNoticeStatus,
    previewUserNoticeAudience,
    sendUserNotice
} from '../../store/actions/userNoticesActions';
import ButtonSpinner from '../_common/ButtonSpinner';
import ConfirmationModal from '../_common/ConfirmationModal';
import NoticeUserPicker from './NoticeUserPicker';
import UserNoticePreview from './UserNoticePreview';
import {PLACEHOLDERS, isHttpsUrl, isRouteKey} from './noticeHelpers';
import './UserNoticeEditor.scss';

const LOCALES = ['pl', 'en'];
const PHASES = ['WARNING', 'BLOCKING'];

const emptyText = () => ({title: '', text: '', actionLabel: ''});
const emptyTexts = () => ({
    WARNING: {pl: emptyText(), en: emptyText()},
    BLOCKING: {pl: emptyText(), en: emptyText()}
});

const selector = state => {
    const {language, userNotices} = state;
    return {
        t: language.translator,
        sendPending: userNotices.sendPending,
        sendResult: userNotices.sendResult,
        previewPending: userNotices.previewPending,
        preview: userNotices.preview,
        error: userNotices.error
    };
};

const UserNoticeEditor = props => {
    const {t, sendPending, sendResult, previewPending, preview, error} = useSelector(selector);
    const dispatch = useDispatch();
    const presetUser = props.location && props.location.state && props.location.state.user;

    const [campaignName, setCampaignName] = useState('');
    const [texts, setTexts] = useState(emptyTexts());
    const [editLocale, setEditLocale] = useState('pl');
    const [editPhase, setEditPhase] = useState('WARNING');
    const [actionType, setActionType] = useState('NONE');
    const [actionTarget, setActionTarget] = useState('');
    const [completion, setCompletion] = useState('ADMIN');
    const [repeatIntervalHours, setRepeatIntervalHours] = useState('24');
    const [blockMode, setBlockMode] = useState('NEVER');
    const [blockAfterDays, setBlockAfterDays] = useState('5');
    const [blockAt, setBlockAt] = useState('');
    const [applyToTabletApp, setApplyToTabletApp] = useState(true);
    const [adminNote, setAdminNote] = useState('');
    const [audienceMode, setAudienceMode] = useState('USERS');
    const [selectedUsers, setSelectedUsers] = useState(presetUser ? [presetUser] : []);
    const [errors, setErrors] = useState([]);
    const [confirming, setConfirming] = useState(false);

    useEffect(() => {
        dispatch(clearUserNoticeStatus());
    }, [dispatch]);

    useEffect(() => {
        if (sendResult) {
            props.history.push({
                pathname: '/user_messages',
                state: {created: sendResult.created === undefined ? 1 : sendResult.created}
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sendResult]);

    const blocks = blockMode !== 'NEVER';
    const current = texts[editPhase][editLocale];
    const single = audienceMode === 'USERS' && selectedUsers.length === 1;

    const setField = (field, value) => {
        setTexts(currentTexts => ({
            ...currentTexts,
            [editPhase]: {
                ...currentTexts[editPhase],
                [editLocale]: {...currentTexts[editPhase][editLocale], [field]: value}
            }
        }));
    };

    const changeBlockMode = mode => {
        setBlockMode(mode);
        if (mode === 'NEVER') {
            setEditPhase('WARNING');
        }
    };

    const buildNotice = () => {
        const list = [];
        PHASES.forEach(phase => {
            if (phase === 'BLOCKING' && !blocks) {
                return;
            }
            LOCALES.forEach(locale => {
                const value = texts[phase][locale];
                if (value.text.trim()) {
                    list.push({
                        locale,
                        platform: 'ANY',
                        phase,
                        text: value.text.trim(),
                        title: value.title.trim() || null,
                        actionLabel: value.actionLabel.trim() || null
                    });
                }
            });
        });
        let days = null;
        if (blockMode === 'DAYS') {
            days = Number(blockAfterDays);
        } else if (blockMode === 'NOW') {
            days = 0;
        }
        return {
            texts: list,
            repeatIntervalHours: Number(repeatIntervalHours),
            blockAfterDays: days,
            blockAfterShows: null,
            blockAt: blockMode === 'AT' && blockAt ? `${blockAt}:00` : null,
            applyToTabletApp,
            adminNote: adminNote.trim() || null,
            actionType,
            actionTarget: actionType === 'NONE' ? null : actionTarget.trim(),
            completion,
            campaignName: campaignName.trim() || null
        };
    };

    const validate = () => {
        const problems = [];
        const hasWarningText = LOCALES.some(locale => texts.WARNING[locale].text.trim());
        if (!hasWarningText) {
            problems.push(t.gettext('Write the message text in at least one language'));
        }
        const hours = Number(repeatIntervalHours);
        if (!Number.isInteger(hours) || hours < 1 || hours > 8760) {
            problems.push(t.gettext('Repeat interval must be between 1 and 8760 hours'));
        }
        if (blockMode === 'DAYS') {
            const days = Number(blockAfterDays);
            if (!Number.isInteger(days) || days < 1 || days > 36500) {
                problems.push(t.gettext('Days until blocking must be between 1 and 36500'));
            }
        }
        if (blockMode === 'AT' && !blockAt) {
            problems.push(t.gettext('Choose the date and time of blocking'));
        }
        if (actionType === 'DEEP_LINK' && !isRouteKey(actionTarget.trim())) {
            problems.push(t.gettext('Screen must be a route key such as profile/verify'));
        }
        if (actionType === 'WEB_PAGE' && !isHttpsUrl(actionTarget.trim())) {
            problems.push(t.gettext('Web page must be an https address'));
        }
        if (audienceMode === 'USERS' && !selectedUsers.length) {
            problems.push(t.gettext('Choose at least one user'));
        }
        if (!single && !campaignName.trim()) {
            problems.push(t.gettext('Campaign name is required when sending to several users'));
        }
        return problems;
    };

    const bulkBody = () => ({
        audience: audienceMode === 'ALL'
            ? {mode: 'ALL', userIds: []}
            : {mode: 'USER_IDS', userIds: selectedUsers.map(user => user.id)},
        notice: buildNotice()
    });

    const review = () => {
        dispatch(clearUserNoticeStatus());
        const problems = validate();
        setErrors(problems);
        if (problems.length) {
            return;
        }
        if (audienceMode === 'ALL') {
            dispatch(previewUserNoticeAudience(bulkBody())).then(action => {
                if (action && action.type === 'USER_NOTICES_PREVIEW_SUCCESS') {
                    setConfirming(true);
                }
            });
        } else {
            setConfirming(true);
        }
    };

    const confirm = () => {
        setConfirming(false);
        if (single) {
            dispatch(sendUserNotice({single: true, body: {...buildNotice(), userId: selectedUsers[0].id}}));
        } else {
            dispatch(sendUserNotice({
                single: false,
                body: {...bulkBody(), expectedCount: audienceMode === 'ALL' && preview ? preview.audienceCount : null}
            }));
        }
    };

    const addUser = user => {
        setSelectedUsers(current => (current.some(item => item.id === user.id) ? current : [...current, user]));
        setErrors([]);
    };

    const removeUser = id => setSelectedUsers(current => current.filter(user => user.id !== id));

    const audienceText = audienceMode === 'ALL'
        ? `${t.gettext('All users')}${preview ? ` (${preview.audienceCount})` : ''}`
        : `${selectedUsers.length} ${t.gettext('user(s)')}`;

    return (
        <div className='userNoticeEditor'>
            <div className='userNoticeEditorHeader'>
                <button className='userNoticeEditorBack' onClick={() => props.history.push('/user_messages')}>
                    <span className='material-icons'>arrow_back</span>
                </button>
                {t.gettext('New message')}
            </div>
            <div className='userNoticeEditorContent'>
                <div className='userNoticeEditorForm'>
                    <section className='userNoticeEditorSection'>
                        <div className='userNoticeEditorSectionHeader'>1 · {t.gettext('Message')}</div>
                        <label className='userNoticeEditorField'>
                            <span>
                                {t.gettext('Campaign name')}
                                {single ? ` (${t.gettext('optional')})` : ''}
                            </span>
                            <input
                                value={campaignName}
                                onChange={event => setCampaignName(event.target.value)}
                                placeholder={t.gettext('Visible only in this panel')}
                                maxLength={120}
                            />
                        </label>
                        <div className='userNoticeEditorTabsRow'>
                            <div className='userNoticeEditorTabs'>
                                {LOCALES.map(locale => (
                                    <button
                                        key={locale}
                                        className={editLocale === locale ? 'userNoticeEditorTab userNoticeEditorTab--active' : 'userNoticeEditorTab'}
                                        onClick={() => setEditLocale(locale)}
                                    >
                                        {locale.toUpperCase()}
                                        {PHASES.some(phase => texts[phase][locale].text.trim()) && ' ✓'}
                                    </button>
                                ))}
                            </div>
                            {blocks &&
                                <div className='userNoticeEditorTabs'>
                                    <button
                                        className={editPhase === 'WARNING' ? 'userNoticeEditorTab userNoticeEditorTab--active' : 'userNoticeEditorTab'}
                                        onClick={() => setEditPhase('WARNING')}
                                    >
                                        {t.gettext('Before blocking')}
                                    </button>
                                    <button
                                        className={editPhase === 'BLOCKING' ? 'userNoticeEditorTab userNoticeEditorTab--active' : 'userNoticeEditorTab'}
                                        onClick={() => setEditPhase('BLOCKING')}
                                    >
                                        {t.gettext('When blocked')}
                                    </button>
                                </div>
                            }
                        </div>
                        <label className='userNoticeEditorField'>
                            <span>{t.gettext('Title')} ({t.gettext('optional')})</span>
                            <input
                                value={current.title}
                                onChange={event => setField('title', event.target.value)}
                                maxLength={200}
                                disabled={sendPending}
                            />
                        </label>
                        <label className='userNoticeEditorField'>
                            <span>{t.gettext('Text')}</span>
                            <textarea
                                rows={4}
                                value={current.text}
                                onChange={event => setField('text', event.target.value)}
                                maxLength={2000}
                                disabled={sendPending}
                            />
                        </label>
                        <label className='userNoticeEditorField'>
                            <span>{t.gettext('Button label')} ({t.gettext('optional')})</span>
                            <input
                                value={current.actionLabel}
                                onChange={event => setField('actionLabel', event.target.value)}
                                maxLength={100}
                                disabled={sendPending}
                            />
                        </label>
                        <div className='userNoticeEditorHint'>
                            {t.gettext('You can use')}: {PLACEHOLDERS.join(' ')}
                            {editPhase === 'BLOCKING' && ` · ${t.gettext('Empty text falls back to the text before blocking')}`}
                        </div>
                    </section>

                    <section className='userNoticeEditorSection'>
                        <div className='userNoticeEditorSectionHeader'>2 · {t.gettext('Button action')}</div>
                        <div className='userNoticeEditorRadios'>
                            {[
                                ['NONE', t.gettext('No action')],
                                ['DEEP_LINK', t.gettext('Open a screen in the app')],
                                ['WEB_PAGE', t.gettext('Open a web page in the app')]
                            ].map(([value, label]) => (
                                <label key={value}>
                                    <input
                                        type='radio'
                                        name='actionType'
                                        checked={actionType === value}
                                        onChange={() => setActionType(value)}
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>
                        {actionType !== 'NONE' &&
                            <label className='userNoticeEditorField'>
                                <span>
                                    {actionType === 'DEEP_LINK' ? t.gettext('Screen') : t.gettext('Web page address')}
                                </span>
                                <input
                                    value={actionTarget}
                                    onChange={event => setActionTarget(event.target.value)}
                                    placeholder={actionType === 'DEEP_LINK' ? 'profile/verify' : 'https://'}
                                    maxLength={actionType === 'DEEP_LINK' ? 200 : 2000}
                                />
                            </label>
                        }
                        <div className='userNoticeEditorSubHeader'>{t.gettext('How does the message end?')}</div>
                        <div className='userNoticeEditorRadios'>
                            <label>
                                <input
                                    type='radio'
                                    name='completion'
                                    checked={completion === 'ADMIN'}
                                    onChange={() => setCompletion('ADMIN')}
                                />
                                {t.gettext('I end it in the panel')}
                            </label>
                            <label>
                                <input
                                    type='radio'
                                    name='completion'
                                    checked={completion === 'USER_ACK'}
                                    onChange={() => setCompletion('USER_ACK')}
                                />
                                {t.gettext('The user confirms in the app')}
                            </label>
                        </div>
                    </section>

                    <section className='userNoticeEditorSection'>
                        <div className='userNoticeEditorSectionHeader'>3 · {t.gettext('Timing')}</div>
                        <label className='userNoticeEditorField userNoticeEditorField--short'>
                            <span>{t.gettext('Show again after (hours)')}</span>
                            <input
                                type='number'
                                min={1}
                                max={8760}
                                value={repeatIntervalHours}
                                onChange={event => setRepeatIntervalHours(event.target.value)}
                            />
                        </label>
                        <div className='userNoticeEditorSubHeader'>{t.gettext('Blocking')}</div>
                        <div className='userNoticeEditorRadios'>
                            {[
                                ['NEVER', t.gettext('Never block, only inform')],
                                ['DAYS', t.gettext('Block after some days from the first display')],
                                ['AT', t.gettext('Block at a chosen date and time')],
                                ['NOW', t.gettext('Block immediately')]
                            ].map(([value, label]) => (
                                <label key={value}>
                                    <input
                                        type='radio'
                                        name='blockMode'
                                        checked={blockMode === value}
                                        onChange={() => changeBlockMode(value)}
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>
                        {blockMode === 'DAYS' &&
                            <label className='userNoticeEditorField userNoticeEditorField--short'>
                                <span>{t.gettext('Days')}</span>
                                <input
                                    type='number'
                                    min={1}
                                    value={blockAfterDays}
                                    onChange={event => setBlockAfterDays(event.target.value)}
                                />
                            </label>
                        }
                        {blockMode === 'AT' &&
                            <label className='userNoticeEditorField userNoticeEditorField--short'>
                                <span>{t.gettext('Date and time')}</span>
                                <input
                                    type='datetime-local'
                                    value={blockAt}
                                    onChange={event => setBlockAt(event.target.value)}
                                />
                            </label>
                        }
                        {blockMode === 'NOW' &&
                            <div className='userNoticeEditorMessage userNoticeEditorMessage--warning'>
                                {t.gettext('The user is blocked on their first request. Use with care.')}
                            </div>
                        }
                        <label className='userNoticeEditorCheckbox'>
                            <input
                                type='checkbox'
                                checked={applyToTabletApp}
                                onChange={event => setApplyToTabletApp(event.target.checked)}
                            />
                            {t.gettext('Also show in the tablet app')}
                        </label>
                        <label className='userNoticeEditorField'>
                            <span>{t.gettext('Internal note')} ({t.gettext('optional')})</span>
                            <input
                                value={adminNote}
                                onChange={event => setAdminNote(event.target.value)}
                                maxLength={500}
                            />
                        </label>
                    </section>

                    <section className='userNoticeEditorSection'>
                        <div className='userNoticeEditorSectionHeader'>4 · {t.gettext('Who gets it')}</div>
                        <div className='userNoticeEditorRadios'>
                            <label>
                                <input
                                    type='radio'
                                    name='audience'
                                    checked={audienceMode === 'USERS'}
                                    onChange={() => setAudienceMode('USERS')}
                                />
                                {t.gettext('Chosen users')}
                            </label>
                            <label>
                                <input
                                    type='radio'
                                    name='audience'
                                    checked={audienceMode === 'ALL'}
                                    onChange={() => setAudienceMode('ALL')}
                                />
                                {t.gettext('All users')}
                            </label>
                        </div>
                        {audienceMode === 'USERS' &&
                            <>
                                <NoticeUserPicker onSelect={addUser}/>
                                <div className='userNoticeEditorChips'>
                                    {selectedUsers.map(user => (
                                        <span key={user.id} className='userNoticeEditorChip'>
                                            {user.name || user.email}
                                            <button
                                                aria-label={t.gettext('Remove')}
                                                onClick={() => removeUser(user.id)}
                                            >
                                                <span className='material-icons'>close</span>
                                            </button>
                                        </span>
                                    ))}
                                    {!selectedUsers.length &&
                                        <span className='userNoticeEditorHint'>{t.gettext('No users chosen')}</span>
                                    }
                                </div>
                            </>
                        }
                        {audienceMode === 'ALL' &&
                            <div className='userNoticeEditorMessage userNoticeEditorMessage--warning'>
                                {t.gettext('Every user will get this message. You will see the number of users before sending.')}
                            </div>
                        }
                    </section>

                    {(errors.length > 0 || error) &&
                        <div className='userNoticeEditorMessage userNoticeEditorMessage--error'>
                            {errors.map(message => <div key={message}>{message}</div>)}
                            {error && <div>{error}</div>}
                        </div>
                    }
                    <div className='userNoticeEditorFooter'>
                        <button
                            className='userNoticeEditorSecondary'
                            onClick={() => props.history.push('/user_messages')}
                            disabled={sendPending}
                        >
                            {t.gettext('Cancel')}
                        </button>
                        <button
                            className='userNoticeEditorPrimary'
                            onClick={review}
                            disabled={sendPending || previewPending}
                        >
                            {(sendPending || previewPending) ? <ButtonSpinner size={20}/> : t.gettext('Review and send')}
                        </button>
                    </div>
                </div>
                <div className='userNoticeEditorAside'>
                    <UserNoticePreview
                        t={t}
                        title={current.title}
                        text={current.text}
                        buttonLabel={current.actionLabel}
                        actionType={actionType}
                        completion={completion}
                        phase={editPhase}
                    />
                </div>
            </div>
            {confirming &&
                <ConfirmationModal
                    text={`${t.gettext('Send this message to')} ${audienceText}?${blockMode === 'NOW' || blockMode === 'AT' ? ` ${t.gettext('It blocks access.')}` : ''}`}
                    closeMethod={() => setConfirming(false)}
                    confirmMethod={confirm}
                    confirmButtonText={t.gettext('Send')}
                />
            }
        </div>
    );
};

export default UserNoticeEditor;
